import React, { useState } from "react";
import { caApi } from "../../../../../backend_urls/caApi";
import SearchDropdown from "../../../models/SearchDropdown";
import FileUpload from "../../../../../components/reusable/FileUpload";
import "../../../../../css/pages/ca_pages/clients/CreateInvoice.css";

const CreateInvoice = ({ isOpen, onClose, onSuccess }) => {
    const [selectedBusiness, setSelectedBusiness] = useState(null);
    const [creationMethod, setCreationMethod] = useState(null);
    const [showFileUpload, setShowFileUpload] = useState(false);
    const [pendingInvoiceFile, setPendingInvoiceFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState("");

    if (!isOpen) { return null; }

    const handleClose = () => {
        if (uploading) { return; }

        setSelectedBusiness(null);
        setCreationMethod(null);
        setShowFileUpload(false);
        setPendingInvoiceFile(null);
        setUploadError("");

        onClose?.();
    };

   const handleBusinessSelect = (business) => {
        console.log("CreateInvoice -> Selected business:", business);

        setSelectedBusiness(business);

        /* Reset dependent fields */
        setCreationMethod(null);
        setShowFileUpload(false);
        setPendingInvoiceFile(null);
        setUploadError("");
    };

    /* Business Clear */
    const handleBusinessClear = () => {
        console.log("CreateInvoice -> Business cleared");

        setSelectedBusiness(null);
        setCreationMethod(null);
        setShowFileUpload(false);
        setPendingInvoiceFile(null);
        setUploadError("");
    };

    /* Creation Method */
    const handleMethodSelect = (method) => {
        if (!selectedBusiness) { return; }

        console.log("CreateInvoice -> Creation method:", method);
        setCreationMethod(method);
        setUploadError("");

        if (method === "upload") { setShowFileUpload(true);
        } else {
            setShowFileUpload(false);
            setPendingInvoiceFile(null);
        }
    };

    /* Upload Label */
    const uploadInvoiceLabel = (business) => {
        if (!business) { return null; }
        return (
            <>
                3. Upload Invoice for{" "}
                <strong>
                    {business.business_name}
                </strong>
            </>
        );
    };

    /* Manual Invoice Label */
    const manualInvoiceLabel = (business) => {
        if (!business) { return null; }
        return (
            <>
                <h3>3. Enter Invoice Details</h3>

                <p>
                    Enter invoice details for{" "}
                    <strong>
                        {business.business_name}
                    </strong>
                    .
                </p>
            </>
        );
    };

    /*
    |--------------------------------------------------------------------------
    | File Selected From FileUpload
    |--------------------------------------------------------------------------
    |
    | IMPORTANT:
    |   This does NOT call the API.
    |   It only stores the File.
    */

    const handleInvoiceFileSelected = (file) => {
        console.log("CreateInvoice -> File selected:", file);

        if (!file) {
            setPendingInvoiceFile(null);
            return;
        }

        setPendingInvoiceFile(file);
        setUploadError("");
    };

    /* Actual API Upload */
    const handleFileUpload = async () => {
        if (!selectedBusiness?.id) {
            setUploadError("Please select a business.");
            return;
        }

        if (!pendingInvoiceFile) {
            setUploadError("Please select an invoice file.");
            return;
        }

        try {
            setUploading(true);
            setUploadError("");

            console.log("CreateInvoice -> Uploading invoice");
            console.log("Business ID:", selectedBusiness.id);
            console.log("Business:", selectedBusiness);
            console.log("File:", pendingInvoiceFile);
            console.log("File name:", pendingInvoiceFile.name);
            console.log("File type:", pendingInvoiceFile.type);
            console.log("File size:", pendingInvoiceFile.size);

            /* API */
            const response = await caApi.createClientInvoice(selectedBusiness.id, pendingInvoiceFile);

            console.log("CreateInvoice -> API response:", response);

            /* API SUCCESS */
            if (onSuccess) { await onSuccess(); }

            /*
            |--------------------------------------------------------------------------
            | Close CreateInvoice
            |--------------------------------------------------------------------------
            */

            setSelectedBusiness(null);
            setCreationMethod(null);
            setShowFileUpload(false);
            setPendingInvoiceFile(null);
            setUploadError("");

            onClose?.();

        } catch (error) {
            console.error(
                "CreateInvoice -> Invoice creation failed:",
                error
            );

            console.error(
                "Response:",
                error?.response
            );

            console.error(
                "Response data:",
                error?.response?.data
            );

            setUploadError(
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error?.message ||
                "Failed to create invoice."
            );

        } finally {
            setUploading(false);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Continue
    |--------------------------------------------------------------------------
    */

    const handleContinue = async () => {
        console.log(
            "CreateInvoice -> Continue clicked"
        );

        console.log(
            "Selected business:",
            selectedBusiness
        );

        console.log(
            "Selected business ID:",
            selectedBusiness?.id
        );

        console.log(
            "Creation method:",
            creationMethod
        );

        console.log(
            "Pending invoice file:",
            pendingInvoiceFile
        );

        if (!selectedBusiness) {
            setUploadError(
                "Please select a business."
            );

            return;
        }

        if (creationMethod === "upload") {
            await handleFileUpload();
            return;
        }

        if (creationMethod === "manual") {
            console.log(
                "Manual invoice flow goes here."
            );

            return;
        }
    };

    return (
        <div
            className="modal-overlay"
            onClick={handleClose}
        >
            <div
                className="modal-container create-invoice-modal"
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                {/* HEADER */}

                <div className="create-invoice-header">
                    <div>
                        <h2>Create Invoice</h2>

                        <p className="form-note">
                            Select a business and choose how you want to create the invoice.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="modal-close-btn"
                        onClick={handleClose}
                        disabled={uploading}
                    >
                        ×
                    </button>
                </div>

                {/* STEP 1 */}
                <div className="create-invoice-section">
                    <h3>1. Select Business</h3>

                    <SearchDropdown
                        searchFunction={ caApi.getCAClientsBusiness }
                        onSelect={ handleBusinessSelect }
                        onClear={ handleBusinessClear }
                        placeholder="Search business..."
                        getText={(business) => business?.business_name || "" }
                        getSubText={(business) => business?.email || "" }
                        getKey={(business) => business?.id }
                        getSelectedValue={(business) => business?.business_name || "" }
                    />

                    {selectedBusiness && (
                        <div className="selected-client-card">
                            <div>
                                <strong>
                                    { selectedBusiness.business_name }
                                </strong>

                                {selectedBusiness.email && (
                                    <span>
                                        { selectedBusiness.email }
                                    </span>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={ handleBusinessClear }
                                disabled={uploading}
                            >
                                Change
                            </button>
                        </div>
                    )}
                </div>

                {/* STEP 2 */}
                <div
                    className={`create-invoice-section ${
                        !selectedBusiness ? "section-disabled" : ""
                    }`}
                >
                    <h3>2. Choose Invoice Creation Method</h3>

                    {!selectedBusiness && (
                        <p className="section-help-text">
                            Please select a business first.
                        </p>
                    )}

                    {selectedBusiness && (
                        <div className="invoice-method-options">
                            {/* UPLOAD */}
                            <button
                                type="button"
                                className={`invoice-method-card ${
                                    creationMethod === "upload" ? "selected" : ""
                                }`}
                                onClick={() => handleMethodSelect("upload") }
                                disabled={uploading}
                            >
                                <div className="method-icon">↑</div>

                                <div className="method-content">
                                    <h4>Upload Invoice Document</h4>

                                    <p>
                                        Upload an existing invoice document in Image or PDF format.
                                    </p>
                                </div>
                            </button>

                            {/* MANUAL */}
                            <button
                                type="button"
                                className={`invoice-method-card ${
                                    creationMethod === "manual" ? "selected" : ""
                                }`}
                                onClick={() => handleMethodSelect("manual") }
                                disabled={uploading}
                            >
                                <div className="method-icon">✎</div>

                                <div className="method-content">
                                    <h4>Enter Invoice Manually</h4>

                                    <p>
                                        Enter the invoice details manually using the invoice form.
                                    </p>
                                </div>
                            </button>
                        </div>
                    )}
                </div>

                {/* STEP 3 */}
                {creationMethod && (
                    <div className="create-invoice-section">
                        {/* UPLOAD */}
                        {creationMethod ===
                            "upload" && (
                            <>
                                {showFileUpload && (
                                    <FileUpload
                                        inline={true}
                                        name="invoice_document"
                                        label={uploadInvoiceLabel(selectedBusiness)}
                                        acceptType="both"
                                        maxFiles={1}
                                        maxSizeMB={10}
                                        onChange={(name, file) => handleInvoiceFileSelected(file)} // Continuously syncs file state
                                        onSave={handleInvoiceFileSelected}
                                        onClose={() => setShowFileUpload(false)}
                                    />
                                )}

                                {/* Selected File Information */}
                                {pendingInvoiceFile && (
                                    <div className="selected-invoice-file">
                                        <strong>
                                            Selected file:
                                        </strong>

                                        <span>
                                            { pendingInvoiceFile.name }
                                        </span>
                                    </div>
                                )}
                            </>
                        )}

                        {/* MANUAL */}
                        {creationMethod === "manual" && (
                            <>
                                { manualInvoiceLabel(selectedBusiness) }

                                <p>Manual invoice form will go here.</p>
                            </>
                        )}
                    </div>
                )}

                {/* ERROR */}
                {uploadError && (
                    <p className="file-upload-modal-error">
                        {uploadError}
                    </p>
                )}

                {/* ACTIONS */}
                <div className="modal-actions">
                    <button
                        type="button"
                        className="cancel-btn"
                        onClick={handleClose}
                        disabled={uploading}
                    >
                        Cancel
                    </button>

                    {creationMethod && (
                        <button
                            type="button"
                            className="create-btn"
                            onClick={handleContinue}
                            disabled={
                                uploading ||
                                (
                                    creationMethod === "upload" &&
                                    !pendingInvoiceFile
                                )
                            }
                        >
                            {uploading ? "Creating..." : "Continue"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreateInvoice;
